import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, Zap, Loader2, Sparkles, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface PlanAction {
  id: string;
  horizon: string;
  action: string;
  why: string;
  module: string;
  auto_exec: boolean;
  status: string;
  result?: Record<string, unknown>;
  error?: string;
}

interface GraftonPlan {
  id: string;
  mode: string;
  status: string;
  executive_summary: string;
  actions: PlanAction[];
  created_at: string;
  validated_at: string | null;
  executed_at: string | null;
  validation_token: string;
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  awaiting_validation: { label: "À valider", color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  validated: { label: "Validé", color: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  executed: { label: "Exécuté", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  rejected: { label: "Rejeté", color: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const ACTION_STATUS_META: Record<string, { label: string; color: string }> = {
  pending: { label: "En attente", color: "text-muted-foreground" },
  prepared: { label: "Préparé", color: "text-amber-400" },
  executed: { label: "Exécuté", color: "text-emerald-400" },
  failed: { label: "Échec", color: "text-red-400" },
  skipped: { label: "Ignoré", color: "text-muted-foreground" },
};

export function GraftonPlansSection() {
  const [plans, setPlans] = useState<GraftonPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<GraftonPlan | null>(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/plans/me/recent?limit=10`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setPlans(d.plans || []);
      }
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const decide = async (plan: GraftonPlan, decision: "validate" | "reject") => {
    setActingOn(plan.id);
    try {
      const r = await fetch(`${BACKEND}/api/plans/${plan.validation_token}/${decision}`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("Action refusée");
      toast.success(decision === "validate" ? "Plan validé. Grafton exécute." : "Plan rejeté.");
      await load();
      setSelectedPlan(null);
    } catch (e) {
      toast.error("Erreur lors de l'action");
    } finally {
      setActingOn(null);
    }
  };

  const activePlan = plans.find((p) => p.status === "awaiting_validation");
  const recentPlans = plans.filter((p) => p.status !== "awaiting_validation").slice(0, 4);

  const formatDate = (iso: string) => {
    try {
      return format(new Date(iso), "dd MMM yyyy 'à' HH:mm", { locale: fr });
    } catch {
      return iso;
    }
  };

  const progress = (p: GraftonPlan) => {
    const total = p.actions.length || 1;
    const done = p.actions.filter((a) => a.status === "executed" || a.status === "prepared").length;
    return Math.round((done / total) * 100);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement des plans Grafton…
        </CardContent>
      </Card>
    );
  }

  if (!plans.length) {
    return (
      <Card data-testid="grafton-plans-empty">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-400" /> Plans d'action Grafton
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Aucun plan reçu pour l'instant. Grafton vous en génère un à chaque briefing (8h CET chaque matin) dès que vous avez un objectif défini dans la trésorerie.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="grafton-plans-section">
      {/* Plan actif en attente */}
      {activePlan && (
        <Card data-testid="active-plan-card" className="border-amber-500/40 bg-amber-500/5">
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-400" />
                  Plan d'attaque en attente de validation
                  {activePlan.mode === "xxl" && (
                    <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/40">
                      Mode XXL
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Proposé par Grafton le {formatDate(activePlan.created_at)} · {activePlan.actions.length} actions
                </p>
              </div>
              <Badge className={STATUS_META[activePlan.status].color} variant="outline">
                {STATUS_META[activePlan.status].label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {activePlan.executive_summary && (
              <div className="text-sm border-l-2 border-amber-500/60 pl-3 italic text-muted-foreground" data-testid="active-plan-summary">
                {activePlan.executive_summary}
              </div>
            )}
            <div>
              <div className="text-xs text-muted-foreground mb-2">Aperçu rapide</div>
              <ul className="space-y-1.5" data-testid="active-plan-actions-preview">
                {activePlan.actions.slice(0, 4).map((a) => (
                  <li key={a.id} className="flex items-start gap-2 text-sm">
                    <ChevronRight className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{a.action}</span>
                  </li>
                ))}
                {activePlan.actions.length > 4 && (
                  <li className="text-xs text-muted-foreground pl-6">
                    + {activePlan.actions.length - 4} autres actions
                  </li>
                )}
              </ul>
            </div>
            <div className="flex items-center gap-2 flex-wrap pt-2">
              <Button
                onClick={() => setSelectedPlan(activePlan)}
                variant="secondary"
                size="sm"
                data-testid="view-plan-details-btn"
              >
                Voir le détail complet
              </Button>
              <Button
                onClick={() => decide(activePlan, "validate")}
                disabled={actingOn === activePlan.id}
                data-testid="validate-plan-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              >
                {actingOn === activePlan.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Valider ce plan
              </Button>
              <Button
                onClick={() => decide(activePlan, "reject")}
                disabled={actingOn === activePlan.id}
                variant="outline"
                size="sm"
                data-testid="reject-plan-btn"
                className="gap-2"
              >
                <X className="h-4 w-4" /> Rejeter
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique des plans */}
      {recentPlans.length > 0 && (
        <Card data-testid="recent-plans-card">
          <CardHeader>
            <CardTitle className="text-lg">Plans récents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentPlans.map((p) => {
              const meta = STATUS_META[p.status] || STATUS_META.awaiting_validation;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlan(p)}
                  className="w-full text-left flex items-center justify-between gap-3 py-2 px-3 rounded hover:bg-muted/40 border border-border/40"
                  data-testid={`recent-plan-${p.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Badge className={meta.color} variant="outline">
                      {meta.label}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {p.executive_summary || `Plan du ${formatDate(p.created_at)}`}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.actions.length} actions · {progress(p)}% complété · {formatDate(p.created_at)}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Dialog de détail */}
      <Dialog open={!!selectedPlan} onOpenChange={() => setSelectedPlan(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="plan-detail-dialog">
          {selectedPlan && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-amber-400" />
                  Plan {selectedPlan.mode === "xxl" && "d'attaque "}
                  du {formatDate(selectedPlan.created_at)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                {selectedPlan.executive_summary && (
                  <div className="text-sm border-l-2 border-amber-500/60 pl-3 italic text-muted-foreground">
                    {selectedPlan.executive_summary}
                  </div>
                )}

                {(["court_terme", "moyen_terme", "long_terme"] as const).map((horizon) => {
                  const items = selectedPlan.actions.filter((a) => a.horizon === horizon);
                  if (!items.length) return null;
                  const labels: Record<string, string> = {
                    court_terme: "Court terme (24-72h)",
                    moyen_terme: "Moyen terme (1-4 semaines)",
                    long_terme: "Long terme (1-3 mois)",
                  };
                  return (
                    <div key={horizon}>
                      <div className="text-xs uppercase font-semibold text-amber-400 mb-2">{labels[horizon]}</div>
                      <div className="space-y-2">
                        {items.map((a) => {
                          const statusMeta = ACTION_STATUS_META[a.status] || ACTION_STATUS_META.pending;
                          return (
                            <div
                              key={a.id}
                              className="rounded border border-border/40 p-3 space-y-1"
                              data-testid={`plan-action-${a.id}`}
                            >
                              <div className="flex items-start gap-2">
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] shrink-0 ${a.auto_exec ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/30"}`}
                                >
                                  {a.auto_exec ? "AUTO" : "MANUEL"}
                                </Badge>
                                <div className="text-sm font-medium flex-1">{a.action}</div>
                                <span className={`text-xs shrink-0 ${statusMeta.color}`}>{statusMeta.label}</span>
                              </div>
                              {a.why && <div className="text-xs text-muted-foreground pl-14">{a.why}</div>}
                              {a.error && (
                                <div className="text-xs text-red-400 pl-14 flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" /> {a.error}
                                </div>
                              )}
                              {a.result && Object.keys(a.result).length > 0 && (
                                <div className="text-xs text-emerald-400 pl-14">
                                  → {JSON.stringify(a.result)}
                                </div>
                              )}
                              {a.module && (
                                <div className="text-[11px] text-muted-foreground pl-14 font-mono">
                                  module : {a.module}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {selectedPlan.status === "awaiting_validation" && (
                  <div className="flex items-center gap-2 pt-4 border-t border-border/40">
                    <Button
                      onClick={() => decide(selectedPlan, "validate")}
                      disabled={actingOn === selectedPlan.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                      data-testid="dialog-validate-btn"
                    >
                      {actingOn === selectedPlan.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      Valider ce plan
                    </Button>
                    <Button
                      onClick={() => decide(selectedPlan, "reject")}
                      disabled={actingOn === selectedPlan.id}
                      variant="outline"
                      className="gap-2"
                      data-testid="dialog-reject-btn"
                    >
                      <X className="h-4 w-4" /> Rejeter
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
